import React from 'react';
import styled, { css } from 'styled-components';
import colors from '../../colors';
import typography from '../../typography';
import spacingSystem from '../../spacing';

// Breadcrumb item props
export interface BreadcrumbItemProps {
  /**
   * The content of the breadcrumb item
   */
  children: React.ReactNode;
  
  /**
   * Whether the item is active (current page)
   * @default false
   */
  active?: boolean;
  
  /**
   * The URL for the breadcrumb item
   */
  href?: string;
  
  /**
   * Click handler for the breadcrumb item
   */
  onClick?: (event: React.MouseEvent<HTMLAnchorElement | HTMLSpanElement>) => void;
  
  /**
   * Additional class name
   */
  className?: string;
}

// Breadcrumb props
export interface BreadcrumbProps {
  /**
   * The content of the breadcrumb (BreadcrumbItem components)
   */
  children: React.ReactNode;
  
  /**
   * The separator between breadcrumb items
   * @default '/'
   */
  separator?: React.ReactNode;
  
  /**
   * Additional class name
   */
  className?: string;
  
  /**
   * ARIA label for the breadcrumb
   * @default 'Breadcrumb'
   */
  ariaLabel?: string;
}

// Styled components
const BreadcrumbContainer = styled.nav`
  display: flex;
  align-items: center;
  font-family: ${typography.fontFamily.primary};
  font-size: ${typography.fontSize.sm};
  color: ${colors.neutral[700]};
`;

const BreadcrumbList = styled.ol`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  padding: 0;
  margin: 0;
  list-style: none;
`;

const BreadcrumbListItem = styled.li`
  display: flex;
  align-items: center;
  
  &:last-child {
    .breadcrumb-separator {
      display: none;
    }
  }
`;

const BreadcrumbLink = styled.a<{ active?: boolean }>`
  color: ${props => props.active ? colors.primary[700] : colors.neutral[700]};
  text-decoration: ${props => props.active ? 'none' : 'underline'};
  font-weight: ${props => props.active ? typography.fontWeight.medium : typography.fontWeight.regular};
  cursor: ${props => props.active ? 'default' : 'pointer'};
  
  &:hover {
    color: ${props => props.active ? colors.primary[700] : colors.primary[500]};
  }
`;

const BreadcrumbText = styled.span<{ active?: boolean }>`
  color: ${props => props.active ? colors.primary[700] : colors.neutral[700]};
  font-weight: ${props => props.active ? typography.fontWeight.medium : typography.fontWeight.regular};
`;

const BreadcrumbSeparator = styled.span`
  display: flex;
  align-items: center;
  margin: 0 ${spacingSystem.spacing[8]};
  color: ${colors.neutral[400]};
`;

/**
 * BreadcrumbItem component for individual breadcrumb links
 */
export const BreadcrumbItem: React.FC<BreadcrumbItemProps> = ({
  children,
  active = false,
  href,
  onClick,
  className,
  ...rest
}) => {
  const handleClick = (event: React.MouseEvent<HTMLAnchorElement | HTMLSpanElement>) => {
    if (active) {
      event.preventDefault();
      return;
    }
    
    if (onClick) {
      onClick(event);
    }
  };
  
  return (
    <BreadcrumbListItem className={className} {...rest}>
      {href && !active ? (
        <BreadcrumbLink
          href={href}
          active={active}
          onClick={handleClick}
        >
          {children}
        </BreadcrumbLink>
      ) : (
        <BreadcrumbText active={active}>
          {children}
        </BreadcrumbText>
      )}
    </BreadcrumbListItem>
  );
};

/**
 * Breadcrumb component for showing navigation hierarchy
 */
export const Breadcrumb: React.FC<BreadcrumbProps> & {
  Item: React.FC<BreadcrumbItemProps>;
} = ({
  children,
  separator = '/',
  className,
  ariaLabel = 'Breadcrumb',
  ...rest
}) => {
  // Add separators between items
  const items = React.Children.map(children, (child, index) => {
    if (!React.isValidElement(child)) {
      return child;
    }
    
    return React.cloneElement(child as React.ReactElement, {
      ...child.props,
      children: (
        <>
          {child.props.children}
          <BreadcrumbSeparator className="breadcrumb-separator">
            {separator}
          </BreadcrumbSeparator>
        </>
      ),
    });
  });
  
  return (
    <BreadcrumbContainer
      aria-label={ariaLabel}
      className={className}
      {...rest}
    >
      <BreadcrumbList>
        {items}
      </BreadcrumbList>
    </BreadcrumbContainer>
  );
};

// Attach BreadcrumbItem as a static property of Breadcrumb
Breadcrumb.Item = BreadcrumbItem;

export default Breadcrumb;